/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable object-property-newline */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React from 'react';
import classnames from 'classnames';
import { Config } from '@leverege/plugin';
import { Theme, PortalOverlay, Toast, Flex, Text } from '@leverege/ui-elements';
import { v4 } from '@leverege/base62-util';
import AttributeObjectValidator from "./validator/AttributeObjectValidator.js";
import Attributes from "../Attributes.js";
import AttributeValueEditors from "./AttributeValueEditors.js";
import EditorUtil from "./EditorUtil.js";
import S from './InlineEditor.css';
import SetterUtil from "./SetterUtil.js";
/**
 * Consumes events 
 */
// function consume( e ) { e.preventDefault() }
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function stopEvent(e) {
  e.stopPropagation();
}
const EMPTY = {};
const ABSOLUTE = {
  top: 0,
  left: 0,
  width: 0,
  height: 0,
  padding: 0,
  margin: 0,
  visible: 'hidden',
  position: 'absolute'
};

/**
 * This will supply a mechanism to display an editor on top of/in replacement of a renderer.
 * The renderer is supplied as the children prop. The inProgress component can be set using
 * Plugin's Config.set( 'AttributeInlineEditor', 'inProgress', Component )
 * The props accepted are  
 * @param {Array} children the renderer
 * @param {ObjectRef} object the object ref. {type, id, ref, data }
 * @param {String} attrName the attribute Name
 * @pamra {object} context the context
 * @param {String} [objectType] The object type, used if object is null.
 */
export default class InlineEditor extends React.Component {
  static contextType = Theme.Context;
  constructor(props) {
    super(props);
    this.ref = /*#__PURE__*/React.createRef();
    this.state = {
      editMode: false,
      inProgress: false,
      errors: {}
    };
    this.longPress = createLongPress(this.onStartEditing);
    this.transaction = null;
    this.editorId = v4().replace(/[0-9]/g, '');
    const attribute = Attributes.getAttribute(props.attrName, props.object?.type);
    this.validator = new AttributeObjectValidator({
      attributes: [attribute],
      object: props.object,
      context: props.context
    });
  }

  /**
   * Remove event listener and cancel long timer on unmount
   */
  componentWillUnmount() {
    window.removeEventListener('keydown', this.onWindowKey);
    this.longPress.cancel();
    this.isUnmounted = true;
  }

  /**
   * Stop editing on an escape key press
   */
  onWindowKey = e => {
    if (e?.key === 'Escape') {
      this.onStopEditing();
    }
  };

  /**
   * Arm the editor for a two-step click to edit
   */
  onMouseDown = e => {
    // Note, if focus is coming from outside the window, it seems (on chrome) the
    // focus occurs before the mouse down is received.
    if (document.activeElement === this.ref.current) {
      this.upWillEdit = true;
      this.longPress.cancel(e);
    } else {
      this.upWillEdit = false;
      this.longPress.start(e);
    }
  };

  /**
   * If second click occurs and we are in step two of the flow, activate the editor
   */
  onClick = e => {
    const {
      singleClick = true
    } = this.props;
    if (singleClick || this.upWillEdit) {
      this.onStartEditing(e);
    }
  };

  /**
   * If we have focus, activate the editing on space or enter key
   */
  onKeyPress = e => {
    if (e?.target === this.ref.current && e?.type === 'keypress' && (e.charCode === 32 || e.key === 'Enter')) {
      e.preventDefault();
      this.onStartEditing(e);
    }
  };

  /**
   * Embedded child l
   */
  onBlur = e => {
    if (!e?.currentTarget.contains(e.relatedTarget)) {
      // Not triggered when swapping focus between children
      const {
        editMode
      } = this.state;
      if (editMode) {
        this.setState({
          editMode: false
        });
      }
    }
  };

  /**
   * Is the tgt in the parentage of ref.
   */
  isParent(tgt) {
    const root = this.ref.current;
    let c = tgt;
    while (c != null) {
      if (c === root) {
        return true;
      }
      c = c.parentElement;
    }
    return false;
  }

  /**
   * Enter editing mode
   */
  onStartEditing = (e, isLongPress) => {
    if (!this.isParent(e?.target || e?.sourceEvent?.target)) {
      return;
    }
    this.setState({
      editMode: true
    });
    window.addEventListener('keydown', this.onWindowKey);
  };

  /**
   * Exit editing mode 
   */
  onStopEditing = (e = EMPTY) => {
    if (e?.type === 'keydown' && e?.key !== 'Escape') {
      return;
    }
    this.setState({
      editMode: false
    });
    window.removeEventListener('keydown', this.onWindowKey);

    // Attempt to regain focus
    setTimeout(() => {
      this.ref.current?.focus();
    }, 10);
  };

  /**
   * Invoked on a change from te editor. This will runt the transaction
   */
  onEditorChange = async evt => {
    const {
      object,
      context
    } = this.props;
    const {
      data: attrName,
      continued,
      event
    } = evt;
    this.setState({
      inProgress: true
    });
    const {
      value: nValue,
      changes
    } = SetterUtil.createDataChange(evt, {});
    const {
      error
    } = await this.validator.validate({
      data: attrName,
      value: nValue
    });
    if (error) {
      this.setState({
        inProgress: false,
        errors: error.flatten()
      });
      Toast.error(['Validation Error', /*#__PURE__*/_jsx(Flex, {
        variant: "colM",
        children: error.issues.map(issue => /*#__PURE__*/_jsx(Text, {
          children: issue.message
        }))
      })]);
    } else {
      // Allow a single setter to fire updates on multiple items
      this.transaction = SetterUtil.toTransaction(object, changes, null, context, this.transaction); // Attributes.set( attrName, object, value, context, this.transaction || true )
      if (continued !== true) {
        if (this.transaction !== true && typeof this.transaction.run === 'function') {
          // eslint-disable-next-line no-console
          this.transaction.run()
          // eslint-disable-next-line no-console
          .catch(console.error).finally(() => {
            if (this.isUnmounted !== true) {
              this.setState({
                inProgress: false,
                errors: {}
              });
            }
          });
        }
        this.onStopEditing(event);
        this.transaction = null;
      } else {
        this.setState({
          inProgress: false,
          errors: {}
        });
      }
    }
  };
  renderEditor = usageOpts => {
    const {
      attrName,
      object,
      objectType,
      context,
      usage,
      children: pChildren,
      style,
      variant
    } = this.props;
    const {
      errors,
      inProgress
    } = this.state;
    const {
      blockChildren = true
    } = usageOpts;
    const e = AttributeValueEditors.getEditorFor(attrName, object || objectType);
    const attribute = Attributes.getAttribute(attrName, object?.type || objectType);
    const canSet = context.editable && Attributes.canSet(attrName, object, undefined, context);
    const editable = canSet;
    const children = blockChildren ? null : pChildren;
    const onChange = this.onEditorChange;
    if (e && attribute) {
      const value = attribute.get(object.data);
      const editor = e.create({
        attribute,
        canSet,
        children,
        context,
        editable,
        error: errors?.fieldErrors?.[attrName],
        eventData: attribute.name,
        inProgress,
        object,
        onChange,
        onClose: this.onStopEditing,
        style,
        usage,
        value,
        variant
      });
      if (editor == null) {
        return null;
      }
      // Attemtp to stop events from going through to other items like the table row
      return editor;
      //  This was here before to stop events from closing the editor. It does
      // not seem needed in new scheme, and this seems to let the table's right click work
      // return ( 
      //   <div
      //     onClick={stopEvent} 
      //     onDoubleClick={stopEvent}
      //     onTouchStart={stopEvent}
      //     onTouchEnd={stopEvent}
      //     onMouseDown={stopEvent}
      //     onMouseUp={stopEvent}
      //     onContextMenu={stopEvent}>
      //     {editor}
      //   </div>
      // )
    }
    return null;
  };
  renderPopup(usageOpts) {
    const pos = this.ref.current.getBoundingClientRect();
    const left = `${pos.left + document.body.scrollLeft}px`;
    const top = `${pos.top + document.body.scrollTop}px`;
    const renderer = this.renderEditor(usageOpts);
    if (renderer == null) {
      return null;
    }
    return /*#__PURE__*/_jsx(PortalOverlay, {
      show: true,
      position: null,
      variant: "attributeValueEditor",
      showBackground: true,
      animation: "fade",
      onClose: this.onStopEditing,
      children: /*#__PURE__*/_jsx("div", {
        style: {
          ...ABSOLUTE,
          top,
          left
        },
        children: renderer
      })
    });
  }
  render() {
    const {
      editMode,
      inProgress
    } = this.state;
    const {
      editable,
      children,
      object,
      attrName,
      context,
      objectType,
      usage,
      inProgressComponent,
      variant
    } = this.props;
    const canSet = editable && Attributes.canSet(attrName, object, undefined, context);
    const editor = AttributeValueEditors.getEditorFor(attrName, object || objectType);
    if (editor == null) {
      return children;
    }
    const usageOpts = editor.getOptions?.(usage, this.props) || EMPTY;
    const {
      isRenderer = false,
      embed = false
    } = usageOpts;

    // Not a renderer and cannot set, use the child renderer
    if (!isRenderer && !canSet) {
      return children;
    }
    if (isRenderer) {
      return /*#__PURE__*/_jsx("div", {
        className: classnames(S.inline, S.renderer, usage && S[`inline${usage.charAt(0).toUpperCase()}${usage.slice(1)}`], {
          [S.editing]: editable
        }),
        "data-editable": canSet && editable,
        children: this.renderEditor(usageOpts)
      });
    }
    if (!editable) {
      return children;
    }
    const IPC = inProgress !== true ? null : inProgressComponent === undefined ? Config.get('AttributeInlineEditor', 'inProgress', null) : inProgressComponent;
    const theme = this.context;
    const cn = theme.classes('Pane', 'pane', EditorUtil.getVariant(usage, 'inline', 'editor', variant), S.inline, usage && S[`inline${usage.charAt(0).toUpperCase()}${usage.slice(1)}`], canSet && S.isEditable, editable && S.editing);
    return /*#__PURE__*/_jsxs("div", {
      id: this.editorId,
      ref: this.ref,
      tabIndex: "0",
      "data-editable": canSet && editable,
      className: cn,
      ...this.longPress.handlers,
      onMouseDown: this.onMouseDown,
      onClick: this.onClick,
      onBlur: embed ? this.onBlur : null,
      onDoubleClick: stopEvent,
      onKeyPress: this.onKeyPress,
      children: [IPC && /*#__PURE__*/_jsx(IPC, {}), IPC ? null : editMode && embed ? this.renderEditor(usageOpts) : children, IPC == null && editMode && !embed && this.renderPopup(usageOpts)]
    });
  }
}
function createLongPress(cb, delay = 750) {
  let timer = null;
  const start = e => {
    e.persist();
    timer = setTimeout(cb, delay, e, true);
  };
  const cancel = e => {
    clearTimeout(timer);
  };
  const handlers = {
    onTouchStart: start,
    onTouchEnd: cancel,
    onMouseDown: start,
    onMouseUp: cancel,
    onMouseLeave: cancel,
    onMouseOut: cancel
  };
  return {
    handlers,
    start,
    cancel
  };
}